/* eslint-disable no-param-reassign */
import { pickBy, identity } from "lodash";
import { api } from "rdx/services/api";
import { actions } from "rdx";
import SchemaValidator from "util/schema-helpers/SchemaValidator";
import { camelizeKeys, keysToSnakeCase } from "lib/helpers/convertObjKeys";
import messages from "./messages";
import {
  GetTeamUsersCsvParamsT,
  GetTeamUsersCsvResponseT,
  GetCsvReportByIdResponseT,
  GetOrgProjectsCsvParamsT,
  GetOrgProjectsCsvResponseT,
  GetAmbassadorsCsvResponseT,
  GetAmbassadorsCsvParamsT,
  GetEarningsAnalyticsCsvParamsT,
  GetEarningsAnalyticsCsvResponseT,
  GetTeamIntelligenceCsvParamsT,
  GetTeamIntelligenceCsvResponseT,
} from "./types";
import {
  GetTeamUsersCsvResponseSchema,
  GetCsvReportByIdResponseSchema,
  GetOrgProjectsCsvResponseSchema,
  GetAmbassadorsCsvResponseSchema,
  GetEarningsAnalyticsCsvResponseSchema,
  GetTeamIntelligenceCsvResponseSchema,
} from "./schemas";
import { CsvJobStateEnum } from "./enums";
import { CSV_REPORTS_TAGS } from "./tags";

const defaultTeamSellersQueryParams = {
  role: ["consultants", "seller_pro", "enterprise_owner"],
  sort: "newest",
};

export const csvReportsApi = api
  .enhanceEndpoints({ addTagTypes: [...Object.values(CSV_REPORTS_TAGS)] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getTeamUsersCsv: builder.query<GetTeamUsersCsvResponseT["body"], GetTeamUsersCsvParamsT>({
        query: ({ filterParams, searchedUserId, isEnterprise, teamType }) => ({
          url: "users/team_csv",
          params: keysToSnakeCase({
            ...(isEnterprise ? {} : defaultTeamSellersQueryParams),
            ...filterParams,
            ...(searchedUserId && { searchedUserId }),
            isEnterprise,
            teamType,
          }),
        }),
        transformResponse: (res: GetTeamUsersCsvResponseT) => {
          SchemaValidator.validate<typeof GetTeamUsersCsvResponseSchema>({
            schema: GetTeamUsersCsvResponseSchema,
            schemaName: "GetTeamUsersCsvResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(({ data }) => {
              if (data.csv.url) {
                dispatch(
                  actions.setAlertMessageVisible({
                    message: messages.GET_CSV_SUCCESS,
                    severity: "success",
                  }),
                );
              }
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_GETTING_CSV,
                  severity: "error",
                }),
              );
            });
        },
        providesTags: [CSV_REPORTS_TAGS.TEAM_USERS_CSV],
      }),
      getOrgProjectsCsv: builder.query<GetOrgProjectsCsvResponseT["body"], GetOrgProjectsCsvParamsT>({
        query: ({ filterParams, tab }) => ({
          url: "enterprise/projects_csv",
          params: keysToSnakeCase({
            ...(tab !== "completed" && {
              status: "active",
            }),
            ...pickBy(filterParams, identity),
            tab,
          }),
        }),
        transformResponse: (res: GetOrgProjectsCsvResponseT) => {
          SchemaValidator.validate<typeof GetOrgProjectsCsvResponseSchema>({
            schema: GetOrgProjectsCsvResponseSchema,
            schemaName: "GetOrgProjectsCsvResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(({ data }) => {
              if (data.csv.url) {
                dispatch(
                  actions.setAlertMessageVisible({
                    message: messages.GET_CSV_SUCCESS,
                    severity: "success",
                  }),
                );
              }
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_GETTING_CSV,
                  severity: "error",
                }),
              );
            });
        },
        providesTags: [CSV_REPORTS_TAGS.ORG_PRE_SALES_CSV],
      }),
      getAmbassadorsCsv: builder.query<GetAmbassadorsCsvResponseT["body"], GetAmbassadorsCsvParamsT>({
        query: () => ({
          url: "users/ambassador_csv",
          params: {
            role: ["ambassador"],
          },
        }),
        transformResponse: (res: GetAmbassadorsCsvResponseT) => {
          SchemaValidator.validate<typeof GetAmbassadorsCsvResponseSchema>({
            schema: GetAmbassadorsCsvResponseSchema,
            schemaName: "GetAmbassadorsCsvResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(({ data }) => {
              if (data.csv.url) {
                dispatch(
                  actions.setAlertMessageVisible({
                    message: messages.GET_CSV_SUCCESS,
                    severity: "success",
                  }),
                );
              }
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_GETTING_CSV,
                  severity: "error",
                }),
              );
            });
        },
        providesTags: [CSV_REPORTS_TAGS.ORG_PRE_SALES_CSV],
      }),
      getEarningsAnalyticsCsv: builder.query<GetEarningsAnalyticsCsvResponseT["body"], GetEarningsAnalyticsCsvParamsT>({
        query: ({ timeFrame }) => ({
          url: "/dashboard_earnings/intelligence",
          params: keysToSnakeCase({ timeFrame, csv: true }),
        }),
        transformResponse: (res: GetEarningsAnalyticsCsvResponseT) => {
          SchemaValidator.validate<typeof GetEarningsAnalyticsCsvResponseSchema>({
            schema: GetEarningsAnalyticsCsvResponseSchema,
            schemaName: "GetEarningsAnalyticsCsvResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(({ data }) => {
              if (data.csv.url) {
                dispatch(
                  actions.setAlertMessageVisible({
                    message: messages.GET_CSV_SUCCESS,
                    severity: "success",
                  }),
                );
              }
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_GETTING_CSV,
                  severity: "error",
                }),
              );
            });
        },
        providesTags: [CSV_REPORTS_TAGS.EARNINGS_ANALYTICS_CSV],
      }),
      getTeamIntelligenceCsv: builder.query<GetTeamIntelligenceCsvResponseT["body"], GetTeamIntelligenceCsvParamsT>({
        query: ({ filterParams }) => ({
          url: "team_intelligence/csv",
          params: keysToSnakeCase({ ...filterParams }),
        }),
        transformResponse: (res: GetTeamIntelligenceCsvResponseT) => {
          SchemaValidator.validate<typeof GetTeamIntelligenceCsvResponseSchema>({
            schema: GetTeamIntelligenceCsvResponseSchema,
            schemaName: "GetTeamIntelligenceCsvResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(({ data }) => {
              if (data.csv.url) {
                dispatch(
                  actions.setAlertMessageVisible({
                    message: messages.GET_CSV_SUCCESS,
                    severity: "success",
                  }),
                );
              }
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_GETTING_CSV,
                  severity: "error",
                }),
              );
            });
        },
        providesTags: [CSV_REPORTS_TAGS.TEAM_INTELLIGENCE_CSV],
      }),
      getCsvReportById: builder.query<GetCsvReportByIdResponseT["body"], { reportId: number }>({
        query: ({ reportId }) => ({
          url: `reports/${reportId}`,
          params: {},
        }),
        transformResponse: (res: GetCsvReportByIdResponseT) => {
          SchemaValidator.validate<typeof GetCsvReportByIdResponseSchema>({
            schema: GetCsvReportByIdResponseSchema,
            schemaName: "GetCsvReportByIdResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(({ data }) => {
              if (data.report.jobStatus === CsvJobStateEnum.ERROR) {
                dispatch(
                  actions.setAlertMessageVisible({
                    message: messages.ERROR_GETTING_CSV,
                    severity: "error",
                    duration: 6000,
                  }),
                );
              }
              if (data.report.url) {
                dispatch(
                  actions.setAlertMessageVisible({
                    message: messages.GET_CSV_SUCCESS,
                    severity: "success",
                  }),
                );
              }
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_GETTING_CSV,
                  severity: "error",
                }),
              );
            });
        },
      }),
    }),
  });
